import React, { useId } from 'react';
import { Lock } from 'lucide-react';
import { resolveStampIcon } from './registry';

type StampProps = {
  /** Achievement title — the italic name in the medallion center (2-3 words). */
  name: string;
  /** Venue name, used for icon resolution. */
  locationName?: string;
  /** Accepted for caller convenience; not rendered. */
  region?: string;
  category?: string;
  /** Earned = full color. Disabled/locked = warm-grayscale, 50% opacity. */
  earned?: boolean;
  multiplier?: number | null;
  /** Year shown as EST. in the medallion. Defaults to brand founding year. */
  year?: number;
  /** Pixel size of the round medallion. */
  size?: number;
  className?: string;
  onClick?: () => void;
};

const OUTER_TEXT = 'ARANOR TRAVEL ARMENIA';

// Year the brand was established (shown as EST. in the medallion).
const EST_YEAR = 2025;

// Shorten an achievement title to 2-3 words for the medallion center.
// Tolerates a missing/empty name so a single unresolved stamp can never
// crash the whole page (renders a blank medallion instead).
const trimTitle = (s?: string | null) =>
  (s ?? '').trim().split(/\s+/).slice(0, 3).join(' ');

/**
 * Circular passport-stamp medallion: curved ARANOR TRAVEL ARMENIA across
 * the top arc, dashed perforation ring, a detailed themed icon, the italic
 * achievement name with EST. year, an optional ×N multiplier badge, and a
 * faded warm-grayscale + padlock treatment when not earned.
 */
const Stamp: React.FC<StampProps> = ({
  name,
  locationName,
  category,
  earned = false,
  multiplier,
  year = EST_YEAR,
  size = 200,
  className = '',
  onClick,
}) => {
  const renderIcon = resolveStampIcon(name, locationName, category);
  const uid = useId().replace(/:/g, '');
  const outerPathId = `outer-${uid}`;

  const title = trimTitle(name);
  // Keep the italic name inside the inner ring (~118px usable width).
  // DM Serif italic averages ~0.5em per char; scale font down for long titles.
  const MAX_TITLE_WIDTH = 118;
  const titleFontSize = Math.min(
    18,
    Math.max(11, MAX_TITLE_WIDTH / (title.length * 0.5)),
  );

  const ink = earned ? '#2b2220' : '#a39189';
  const fill = earned ? '#ffc888' : '#e8d8cf';
  const fillSoft = earned ? '#ffd9a8' : '#efe4dc';

  const showMultiplier = earned && multiplier != null && multiplier > 1;

  return (
    <div
      onClick={onClick}
      className={`relative inline-flex flex-col items-center ${
        onClick ? 'cursor-pointer transition-transform hover:-translate-y-1' : ''
      } ${className}`}
      style={{ width: size }}
    >
      <svg
        viewBox="0 0 200 200"
        width={size}
        height={size}
        className={earned ? '' : 'opacity-50'}
        role="img"
        aria-label={`${name}${earned ? '' : ' (locked)'}`}
      >
        <defs>
          {/* Half-circle arc: 9 o'clock → over the top → 3 o'clock.
              startOffset 50% therefore sits dead-center on the top arc,
              reading left-to-right. */}
          <path
            id={outerPathId}
            d="M22,100 A78,78 0 0 1 178,100"
          />
        </defs>

        {/* Disc */}
        <circle cx="100" cy="100" r="92" fill={fill} />
        <circle
          cx="100"
          cy="100"
          r="92"
          fill="none"
          stroke={ink}
          strokeWidth="2.5"
        />

        {/* Curved outer text — single pass, centered on the top arc */}
        <text
          fontFamily="Montserrat, sans-serif"
          fontSize="12"
          fontWeight="700"
          letterSpacing="4"
          fill={ink}
        >
          <textPath
            href={`#${outerPathId}`}
            startOffset="50%"
            textAnchor="middle"
          >
            {OUTER_TEXT}
          </textPath>
        </text>

        {/* Dashed perforation ring */}
        <circle
          cx="100"
          cy="100"
          r="74"
          fill="none"
          stroke={ink}
          strokeWidth="1.5"
          strokeDasharray="1 5"
          strokeLinecap="round"
        />

        {/* Solid inner ring + soft inner field */}
        <circle cx="100" cy="100" r="68" fill={fillSoft} />
        <circle
          cx="100"
          cy="100"
          r="68"
          fill="none"
          stroke={ink}
          strokeWidth="1.5"
        />

        {/* Themed icon */}
        <g transform="translate(68 50) scale(1)" color={ink}>
          {renderIcon({ width: 64, height: 64 })}
        </g>

        {/* Italic achievement name — auto-sized & clamped to the inner ring */}
        <text
          x="100"
          y="124"
          textAnchor="middle"
          fontFamily="'DM Serif Display', serif"
          fontSize={titleFontSize}
          fontStyle="italic"
          fill={ink}
          textLength={
            title.length * titleFontSize * 0.5 > MAX_TITLE_WIDTH
              ? MAX_TITLE_WIDTH
              : undefined
          }
          lengthAdjust="spacingAndGlyphs"
        >
          {title}
        </text>

        {/* EST. year */}
        <text
          x="100"
          y="144"
          textAnchor="middle"
          fontFamily="Montserrat, sans-serif"
          fontSize="9"
          fontWeight="700"
          letterSpacing="2"
          fill={ink}
          opacity="0.7"
        >
          EST. {year}
        </text>
      </svg>

      {/* Floating multiplier badge */}
      {showMultiplier && (
        <div className="absolute -top-1 right-2 bg-brand-primary text-brand-secondary text-xs font-heading font-bold w-9 h-9 rounded-full flex items-center justify-center shadow-lg ring-2 ring-white">
          &times;{multiplier}
        </div>
      )}

      {/* Locked padlock overlay */}
      {!earned && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="bg-white/70 w-12 h-12 rounded-full flex items-center justify-center shadow-sm backdrop-blur-[1px]">
            <Lock className="w-5 h-5 text-warm-500" />
          </div>
        </div>
      )}
    </div>
  );
};

export default Stamp;
